// Framer Motion Animation Variants
// iOS-style smooth and elegant animations

import { Variants } from 'framer-motion';
import { animations } from './designSystem';

// Container animations with stagger
export const containerVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
      duration: animations.normal / 1000,
    },
  },
};

// Fade up animation for items
export const fadeUpVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: animations.normal / 1000,
      ease: animations.easeOut,
    },
  },
};

// Scale + fade animation
export const scaleVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: animations.normal / 1000,
      ease: animations.easeOut,
    },
  },
};

// Slide from side
export const slideFromLeftVariants: Variants = {
  hidden: {
    x: -300,
    opacity: 0,
  },
  visible: {
    x: 0,
    opacity: 1,
    transition: animations.spring,
  },
  exit: {
    x: -300,
    opacity: 0,
    transition: {
      duration: animations.normal / 1000,
      ease: animations.easeIn,
    },
  },
};

// Modal backdrop
export const backdropVariants: Variants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: animations.fast / 1000,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: animations.fast / 1000,
    },
  },
};

// Modal content
export const modalVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.9,
    y: 20,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: animations.normal / 1000,
      ease: animations.easeOut,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    y: 20,
    transition: {
      duration: animations.fast / 1000,
      ease: animations.easeIn,
    },
  },
};

// Gallery image transitions
export const galleryImageVariants: Variants = {
  enter: {
    opacity: 0,
  },
  center: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn',
    },
  },
};

// Button hover/tap animations
export const buttonVariants = {
  hover: {
    scale: 1.05,
    y: -2,
    transition: {
      duration: animations.fast / 1000,
      ease: animations.easeOut,
    },
  },
  tap: {
    scale: 0.95,
    transition: {
      duration: animations.fast / 1000,
      ease: animations.easeIn,
    },
  },
};

// Card hover animation
export const cardHoverVariants = {
  hover: {
    scale: 1.02,
    y: -4,
    transition: {
      duration: animations.fast / 1000,
      ease: animations.easeOut,
    },
  },
  tap: {
    scale: 0.98,
    transition: {
      duration: animations.fast / 1000,
      ease: animations.easeIn,
    },
  },
};

// Floating animation for AI card
export const floatingVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 50,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.7,
      duration: 0.6,
      ease: animations.easeOut,
    },
  },
};

// Accordion expand/collapse
export const accordionVariants: Variants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition: {
      duration: animations.fast / 1000,
      ease: animations.easeIn,
    },
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    transition: {
      duration: animations.normal / 1000,
      ease: animations.easeOut,
    },
  },
};

// Tab transition
export const tabVariants: Variants = {
  enter: {
    x: 20,
    opacity: 0,
  },
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: animations.normal / 1000,
      ease: animations.easeOut,
    },
  },
  exit: {
    x: -20,
    opacity: 0,
    transition: {
      duration: animations.fast / 1000,
      ease: animations.easeIn,
    },
  },
};

// List item stagger
export const listItemVariants: Variants = {
  hidden: {
    opacity: 0,
    x: -10,
  },
  visible: (custom: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: custom * 0.05,
      duration: animations.normal / 1000,
      ease: animations.easeOut,
    },
  }),
};

// Pulse animation (for loading states)
export const pulseVariants: Variants = {
  pulse: {
    opacity: [0.6, 1, 0.6],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

// Shake animation (for errors)
export const shakeVariants: Variants = {
  shake: {
    x: [0, -10, 10, -10, 10, 0],
    transition: {
      duration: 0.5,
      ease: 'easeInOut',
    },
  },
};

// Bottom sheet animation (slides from bottom)
export const bottomSheetVariants: Variants = {
  hidden: {
    y: '100%',
  },
  visible: {
    y: 0,
    transition: {
      type: 'spring',
      damping: 30,
      stiffness: 300,
    },
  },
  exit: {
    y: '100%',
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};
